import { useMemo } from 'react'
import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { invalidateRegistrationWizardMetadata } from '@/admin/lib/wizard-metadata-cache'
import { useOrg } from '@/admin/hooks/use-org'
import { usePermissions } from '@/admin/hooks/use-permissions'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type {
  ProductTemplate,
  ProductAttribute,
  ProductBrand,
  ProductCategory,
  Product,
  Holding,
  FieldMeta,
  PaginatedResponse,
  RegistrationFormMetadata,
  RegistrationResponse,
  ConsumerAccountSummary,
  ConsumerWithHoldings,
} from '@/admin/types/products'

// ── Templates ────────────────────────────────────────────

// Exported as shared query options so the post-login cache warmer can prefetch
// this exact query without re-declaring the key/queryFn (no drift).
export function productTemplatesQueryOptions() {
  return queryOptions<ProductTemplate[]>({
    queryKey: ['product-templates'],
    queryFn: () =>
      api
        .get<PaginatedResponse<ProductTemplate>>('/products/templates/', {
          params: { page_size: 200 },
        })
        .then(r => r.data.results),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export function useProductTemplates(options?: { enabled?: boolean }) {
  // ProductTemplateListCreateView enforces `product-templates.view` on GET.
  const { can } = usePermissions()
  return useQuery<ProductTemplate[]>({
    ...productTemplatesQueryOptions(),
    enabled: (options?.enabled ?? true) && can('product-templates.view'),
  })
}

export function useProductTemplate(uuid: string | null) {
  return useQuery<ProductTemplate>({
    queryKey: ['product-template', uuid],
    queryFn: () => api.get(`/products/templates/${uuid}/`).then(r => r.data),
    enabled: !!uuid,
  })
}

export function useCreateProductTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ProductTemplate>) =>
      api.post('/products/templates/', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

export function useUpdateProductTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: Partial<ProductTemplate> & { uuid: string; host_template_uuid?: string | null; host_requirement?: string; transaction_template_uuid?: string | null }) =>
      api.put(`/products/templates/${uuid}/`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      qc.invalidateQueries({ queryKey: ['product-template', vars.uuid] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

export function useDeleteProductTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, force = false }: { uuid: string; force?: boolean }) =>
      api.delete(`/products/templates/${uuid}/`, {
        params: force ? { force: true } : undefined,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      qc.removeQueries({ queryKey: ['product-template', vars.uuid] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

// ── Template Attributes ──────────────────────────────────

export function useTemplateAttributes(templateUuid: string | null) {
  return useQuery<ProductAttribute[]>({
    queryKey: ['template-attributes', templateUuid],
    queryFn: () =>
      api.get(`/products/templates/${templateUuid}/attributes/`).then(r => r.data),
    enabled: !!templateUuid,
  })
}

export function useCreateTemplateAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      ...data
    }: Partial<ProductAttribute> & { templateUuid: string }) =>
      api
        .post(`/products/templates/${templateUuid}/attributes/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['template-attributes', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['product-template', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      qc.invalidateQueries({ queryKey: ['product-form-metadata'] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

export function useUpdateTemplateAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attrUuid,
      ...data
    }: Partial<ProductAttribute> & { templateUuid: string; attrUuid: string }) =>
      api
        .put(`/products/templates/${templateUuid}/attributes/${attrUuid}/`, data)
        .then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['template-attributes', vars.templateUuid] })
      // The templates page renders attribute rows (and their
      // configuration_data) from the plural list query. Without this the
      // reopened edit dialog shows pre-save config, and saving from it writes
      // that stale config back over what was just saved.
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      qc.invalidateQueries({ queryKey: ['product-form-metadata'] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

export function useDeleteTemplateAttribute() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attrUuid,
      force,
    }: {
      templateUuid: string
      attrUuid: string
      force?: boolean
    }) =>
      api.delete(`/products/templates/${templateUuid}/attributes/${attrUuid}/`, {
        params: force ? { force: 'true' } : undefined,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['template-attributes', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['product-template', vars.templateUuid] })
      qc.invalidateQueries({ queryKey: ['product-templates'] })
      qc.invalidateQueries({ queryKey: ['product-form-metadata'] })
      invalidateRegistrationWizardMetadata(qc)
    },
  })
}

export function useReorderTemplateAttributes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      templateUuid,
      attribute_level,
      attributes,
    }: {
      templateUuid: string
      attribute_level: string
      attributes: { uuid: string; order: number }[]
    }) =>
      api.patch(`/products/templates/${templateUuid}/attributes/reorder/`, {
        attribute_level,
        attributes: attributes.map(a => ({ uuid: a.uuid })),
      }),
    // Apply the new order optimistically so the row moves immediately instead
    // of snapping back until the PATCH + refetch completes. Reorder only the
    // affected level's items in place; roll back on error; reconcile on settle.
    onMutate: async ({ templateUuid, attributes }) => {
      const key = ['template-attributes', templateUuid]
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<ProductAttribute[]>(key)
      if (previous) {
        const affected = new Set(attributes.map(a => a.uuid))
        const inNewOrder = attributes
          .map(a => previous.find(p => p.uuid === a.uuid))
          .filter((p): p is ProductAttribute => Boolean(p))
        const positions = previous.reduce<number[]>((acc, p, i) => {
          if (affected.has(p.uuid)) acc.push(i)
          return acc
        }, [])
        if (inNewOrder.length === positions.length) {
          const next = [...previous]
          positions.forEach((pos, i) => {
            next[pos] = inNewOrder[i]
          })
          qc.setQueryData(key, next)
        }
      }
      return { previous }
    },
    // On error, roll back AND force a refetch to recover the true order.
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['template-attributes', vars.templateUuid], ctx.previous)
      }
      qc.invalidateQueries({ queryKey: ['template-attributes', vars.templateUuid] })
    },
    // On success the optimistic order already matches what the server
    // persisted, so DON'T fire an immediate refetch - that post-drop refetch
    // is what made the row visibly snap/lag (WP1-73 issue #2). Mark the query
    // stale (refetchType 'none') so the next natural mount reconciles without
    // a disruptive in-place reload.
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['template-attributes', vars.templateUuid],
        refetchType: 'none',
      })
      // Field order feeds the wizard schema; mark stale, don't refetch (above).
      qc.invalidateQueries({ queryKey: ['holding-form-schema'], refetchType: 'none' })
    },
  })
}

// ── Categories ───────────────────────────────────────────

export function useProductCategories(options?: {
  withProductCount?: boolean
  templateName?: string | null
  templateUuid?: string | null
}) {
  const withProductCount = options?.withProductCount ?? false
  const templateName = options?.templateName ?? null
  const templateUuid = options?.templateUuid ?? null
  return useQuery<ProductCategory[]>({
    queryKey: ['product-categories', { withProductCount, templateName, templateUuid }],
    queryFn: () =>
      api
        .get<PaginatedResponse<ProductCategory>>('/products/categories/', {
          params: {
            page_size: 200,
            ...(withProductCount && { annotate_product_count: 'true' }),
            ...(templateName && { template_name: templateName }),
            ...(templateUuid && { template_uuid: templateUuid }),
          },
        })
        .then((r) => r.data.results),
  })
}

/**
 * Recursively flatten a tree of product categories returned by
 * `useProductCategories`. The endpoint only returns root categories with their
 * children nested, so any consumer that needs to look up a category by UUID
 * (e.g. resolving `category_ids` on an availability rule) must walk the tree.
 */
export function flattenProductCategories(
  categories: ProductCategory[] | undefined,
): ProductCategory[] {
  if (!categories?.length) return []
  const out: ProductCategory[] = []
  const walk = (nodes: ProductCategory[]) => {
    for (const node of nodes) {
      out.push(node)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(categories)
  return out
}

export function useCreateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      slug: string
      description?: string
      parent_category_uuid?: string | null
      sort_order?: number
    }) => api.post('/products/categories/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-categories'] }),
  })
}

export function useUpdateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: { uuid: string } & Record<string, unknown>) =>
      api.put(`/products/categories/${uuid}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-categories'] }),
  })
}

export function useDeleteProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/products/categories/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-categories'] }),
  })
}

// ── Brands ───────────────────────────────────────────────

export function useProductBrands() {
  return useQuery<ProductBrand[]>({
    queryKey: ['product-brands'],
    queryFn: () =>
      api
        .get<PaginatedResponse<ProductBrand>>('/products/brands/', {
          params: { page_size: 200 },
        })
        .then((r) => r.data.results),
  })
}

export function useProductBrand(uuid: string | undefined) {
  return useQuery<ProductBrand>({
    queryKey: ['product-brand', uuid],
    enabled: !!uuid,
    queryFn: () => api.get(`/products/brands/${uuid}/`).then((r) => r.data),
  })
}

export function useCreateProductBrand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      slug: string
      description?: string
      website?: string
      logo_uuid?: string | null
      sort_order?: number
    }) => api.post('/products/brands/', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-brands'] }),
  })
}

export function useUpdateProductBrand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: { uuid: string } & Record<string, unknown>) =>
      api.put(`/products/brands/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-brands'] }),
  })
}

export function useDeleteProductBrand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/products/brands/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-brands'] }),
  })
}

// ── Products ─────────────────────────────────────────────

export function useProducts(
  params?: Record<string, string>,
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<Product>>({
    queryKey: ['products', params],
    queryFn: () =>
      api.get('/products/items/', { params }).then(r => r.data),
    enabled: options?.enabled ?? true,
  })
}

/**
 * Unified filter facets for the product library filter fly-out: the structural
 * fields (template, category, status, type) plus every product-level and
 * variant-level attribute, all driven by the tenant's own configuration.
 * Unlike the consumer facets, this exposes attributes of every visibility
 * (admin surface).
 */
export function useProductFilterFacets() {
  return useQuery<{ facets: ProductFilterFacet[] }>({
    queryKey: ['product-filter-facets'],
    queryFn: () =>
      api.get('/products/items/metadata/filter-facets/').then((r) => r.data),
  })
}

export function useProduct(uuid: string | null) {
  return useQuery<Product>({
    queryKey: ['product', uuid],
    queryFn: () => api.get(`/products/items/${uuid}/`).then(r => r.data),
    enabled: !!uuid,
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      product_template_uuid: string
      attribute_values?: Record<string, unknown>
      category_uuid?: string | null
      image_uuid?: string | null
      status?: string
    }) => api.post('/products/items/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })
}

export function useUpdateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: { uuid: string } & Record<string, unknown>) =>
      api.put(`/products/items/${uuid}/`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['products'] })
      qc.invalidateQueries({ queryKey: ['product', vars.uuid] })
    },
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/products/items/${uuid}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })
}

// ── Variants ─────────────────────────────────────────────

function sortProductsByShortName(products: Product[]): Product[] {
  return [...products].sort((a, b) => {
    const aName = a.cached_short_name?.trim() || ''
    const bName = b.cached_short_name?.trim() || ''
    return aName.localeCompare(bName, undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  })
}

export function useProductVariants(productUuid: string | null, search?: string) {
  const term = search?.trim() ?? ''
  return useQuery<Product[]>({
    queryKey: ['product-variants', productUuid, term],
    queryFn: () =>
      api
        .get(`/products/items/${productUuid}/variants/`, {
          params: term ? { search: term } : undefined,
        })
        .then(r => sortProductsByShortName(r.data.results ?? r.data)),
    enabled: !!productUuid,
  })
}

export function useCreateVariant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      productUuid,
      ...data
    }: {
      productUuid: string
      attribute_values?: Record<string, unknown>
      status?: string
    }) =>
      api.post(`/products/items/${productUuid}/variants/`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['product-variants', vars.productUuid] })
      qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useGenerateVariants() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (productUuid: string) =>
      api.post(`/products/items/${productUuid}/variants/generate/`).then(r => r.data),
    onSuccess: (_, productUuid) => {
      qc.invalidateQueries({ queryKey: ['product-variants', productUuid] })
      qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useVariantFormMetadata(productUuid: string | null) {
  return useQuery<FieldMeta[]>({
    queryKey: ['variant-form-metadata', productUuid],
    queryFn: () =>
      api
        .get(`/products/items/${productUuid}/variants/metadata/`)
        .then(r => r.data),
    enabled: !!productUuid,
  })
}

// ── Form Metadata ────────────────────────────────────────

export function useProductFormMetadata(templateUuid: string | null) {
  return useQuery<FieldMeta[]>({
    queryKey: ['product-form-metadata', templateUuid],
    queryFn: () =>
      api
        .get('/products/items/metadata/form-fields/', {
          params: { template_uuid: templateUuid },
        })
        .then(r => r.data),
    enabled: !!templateUuid,
  })
}

// ── Holdings ─────────────────────────────────────────────

/** BE-declared sortable column: which `ordering` fields the list honours. */
export interface SortableColumn {
  field: string
  label: string
}

export function useHoldings(params?: Record<string, string>) {
  return useQuery<
    PaginatedResponse<Holding> & {
      filter_options?: ProductFilterFacet[]
      sortable_columns?: SortableColumn[]
    }
  >({
    queryKey: ['holdings', params],
    queryFn: () => api.get('/registrations/', { params }).then(r => r.data),
  })
}

export interface HoldingStatusCount {
  key: string
  label: string
  count: number
}

export function useHoldingStatusCounts(params?: Record<string, string>) {
  return useQuery<HoldingStatusCount[]>({
    queryKey: ['holding-status-counts', params],
    queryFn: () =>
      api.get('/registrations/status-counts/', { params }).then((r) => r.data),
  })
}

export function useHolding(uuid: string | null) {
  return useQuery<Holding>({
    queryKey: ['holding', uuid],
    queryFn: () => api.get(`/registrations/${uuid}/`).then(r => r.data),
    enabled: !!uuid,
  })
}

export interface ReassignProductPayload {
  product_uuid?: string
  product_config_key?: string
  reason?: string
  create_warranty?: boolean
}

/**
 * Reassign a holding's product - resolves an unidentified (placeholder)
 * registration to its real product, or corrects a wrong assignment.
 */
export function useReassignHoldingProduct(holdingUuid: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReassignProductPayload) =>
      api
        .put(`/registrations/${holdingUuid}/reassign-product/`, payload)
        .then(r => r.data),
    onSuccess: () => {
      if (holdingUuid)
        qc.invalidateQueries({ queryKey: ['holding', holdingUuid] })
      qc.invalidateQueries({ queryKey: ['holdings'] })
      qc.invalidateQueries({ queryKey: ['claim'] })
    },
  })
}

export interface DuplicateAddressMatch {
  ref: string
  holding_uuid: string
  registration_number?: string | null
  owner_name?: string | null
  owner_first_name?: string | null
  owner_last_name?: string | null
  owner_email?: string | null
  owner_phone?: string | null
  product_name?: string | null
  product_uuid?: string | null
  consumer_account_uuid?: string | null
  structure_values?: Record<string, unknown>
}

export function useDuplicateAddressMatches(
  host: Record<string, unknown> | null,
  options?: { enabled?: boolean },
) {
  return useQuery<DuplicateAddressMatch[]>({
    queryKey: ['duplicate-address-lookup', host],
    queryFn: () =>
      api
        .post('/registrations/duplicate-address-lookup/', { host })
        .then((r) => r.data?.matches ?? []),
    enabled: (options?.enabled ?? true) && !!host,
  })
}

export function useCreateHolding() {
  const qc = useQueryClient()
  return useMutation<RegistrationResponse, Error, {
    product_uuid: string
    partnership_uuid?: string
    consumer_account_uuid?: string
    consumer_email?: string
    consumer_first_name?: string
    consumer_last_name?: string
    consumer_phone_number?: string
    attribute_values?: Record<string, unknown>
    registration_field_values?: Record<string, unknown>
    transaction_data?: Record<string, unknown>
    warranty_template_option_uuid?: string
    active_from?: string
    duplicate_address_resolution?: {
      decision: 'merge' | 'reject'
      issue_id: string
      address_key: string
      reason_text?: string
      resolution_context?: Record<string, unknown> | null
      duplicate_holding_uuids: string[]
    }
    existing_host_uuid?: string
    structure_uuid?: string
    mailing_address?: import('@/shared/lib/formatting').Address | import('@/shared/lib/formatting').AddressValue
    /** Generic consumer EAV values keyed by ConsumerAttribute code_name. */
    consumer_attribute_values?: Record<string, unknown>
    consents?: Record<string, boolean>
  }>({
    mutationFn: (data) => api.post('/registrations/', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['holdings'] }),
  })
}

/** Edit holding attribute values with audit reason. */
export function useEditHolding() {
  const qc = useQueryClient()
  return useMutation<
    unknown,
    Error,
    { uuid: string; attribute_values?: Record<string, unknown>; consumer_fields?: Record<string, string>; edit_reason: string }
  >({
    mutationFn: ({ uuid, attribute_values, consumer_fields, edit_reason }) =>
      api.put(`/registrations/${uuid}/`, { attribute_values, consumer_fields, edit_reason }).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['holding', vars.uuid] })
      qc.invalidateQueries({ queryKey: ['holdings'] })
      qc.invalidateQueries({ queryKey: ['workflows', 'entity-log'] })
    },
  })
}

export interface HoldingFormMetadataOptions {
  installation_date?: string | null
  host_attrs?: Record<string, unknown> | null
  partnership_uuid?: string | null
  /**
   * Holding-level attribute values collected on the product step
   * (e.g. OC's ``pitch_of_roof``, ``roof_total_squares``,
   * ``owens_corning_components``). Forwarded to the backend so warranty
   * availability rules can reference them via ``entity:holding.<code>``
   * or the flat ``field:<code>`` ref. Without this, predicates that
   * gate on these values can never match.
   */
  holding_attrs?: Record<string, unknown> | null
  /**
   * The selected product's template uuid. When supplied, the template-scoped
   * form schema (fields, host/transaction templates, consumer fields) is
   * fetched and cached per *template* rather than per product, so switching
   * between products on the same template is an instant cache hit and the
   * post-login warmer can prefetch it. Omit to fall back to product-keyed
   * schema loading.
   */
  product_template_uuid?: string | null
}

/**
 * Template-scoped registration form schema (fields, host template,
 * transaction template, consumer fields, early attributes).
 *
 * Cached per product template - identical for every product on the template -
 * so it survives product switches and is prewarmable. Exported as shared query
 * options so the warmer reuses this exact key/queryFn (no drift).
 */
export function holdingFormSchemaQueryOptions(productTemplateUuid: string | null) {
  return queryOptions<Partial<RegistrationFormMetadata>>({
    queryKey: ['holding-form-schema', productTemplateUuid],
    queryFn: () =>
      api
        .get('/registrations/metadata/form-schema/', {
          params: { product_template_uuid: productTemplateUuid },
        })
        .then(r => r.data),
    enabled: !!productTemplateUuid,
  })
}

/**
 * Product-scoped warranty availability (the echoed product + resolved warranty
 * templates). Keyed by product + installation date + host/holding attrs +
 * partnership because availability genuinely varies per product instance;
 * deliberately NOT cacheable per template.
 */
export function holdingWarrantyAvailabilityQueryOptions(
  productUuid: string | null,
  options?: HoldingFormMetadataOptions,
) {
  const installationDate = options?.installation_date || null
  const partnershipUuid = options?.partnership_uuid || null
  const hostAttrs = options?.host_attrs ?? null
  const hasHostAttrs = !!hostAttrs && Object.keys(hostAttrs).length > 0
  const hostAttrsKey = hasHostAttrs ? JSON.stringify(hostAttrs) : null
  const holdingAttrs = options?.holding_attrs ?? null
  const hasHoldingAttrs = !!holdingAttrs && Object.keys(holdingAttrs).length > 0
  const holdingAttrsKey = hasHoldingAttrs ? JSON.stringify(holdingAttrs) : null

  return queryOptions<Partial<RegistrationFormMetadata>>({
    queryKey: [
      'warranty-availability',
      productUuid,
      installationDate,
      partnershipUuid,
      hostAttrsKey,
      holdingAttrsKey,
    ],
    queryFn: () =>
      api
        .get('/registrations/metadata/warranty-availability/', {
          params: {
            product_uuid: productUuid,
            ...(installationDate ? { installation_date: installationDate } : {}),
            ...(partnershipUuid ? { partnership_uuid: partnershipUuid } : {}),
            ...(hasHostAttrs ? { host_attrs: hostAttrsKey } : {}),
            ...(hasHoldingAttrs ? { holding_attrs: holdingAttrsKey } : {}),
          },
        })
        .then(r => r.data),
    enabled: !!productUuid,
    // Holding-level attribute values (and host attributes) are part of
    // the query key so the backend can pre-filter the returned warranty
    // templates. They change on every keystroke inside the wizard's
    // dynamic-attribute inputs, which means React Query sees a brand new
    // query for each character and `data` would otherwise drop back to
    // `undefined` until the new request resolves. Holding onto the previous
    // payload until the new one arrives keeps the warranty panel mounted and
    // the form values stable (avoids an entrance-animation replay flicker).
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Registration form metadata for a selected product.
 *
 * Internally split into two independently-cached queries - a template-scoped
 * form schema and a product-scoped warranty availability - then merged back
 * into a single ``RegistrationFormMetadata`` so callers see the same shape as
 * before. The schema half paints instantly from cache (or a prewarm) on
 * product selection while only warranty resolution hits the network.
 */
export function useHoldingFormMetadata(
  productUuid: string | null,
  options?: HoldingFormMetadataOptions,
) {
  const templateUuid = options?.product_template_uuid ?? null
  const schema = useQuery(holdingFormSchemaQueryOptions(templateUuid))
  const warranty = useQuery(
    holdingWarrantyAvailabilityQueryOptions(productUuid, options),
  )

  const data = useMemo<RegistrationFormMetadata | undefined>(() => {
    if (!schema.data && !warranty.data) return undefined
    return {
      ...(schema.data ?? {}),
      ...(warranty.data ?? {}),
    } as RegistrationFormMetadata
  }, [schema.data, warranty.data])

  return {
    data,
    isLoading: schema.isLoading || warranty.isLoading,
    isFetching: schema.isFetching || warranty.isFetching,
    isError: schema.isError || warranty.isError,
    error: schema.error ?? warranty.error,
  }
}

export function useProductSearch(
  search: string,
  options?: { include_variants?: boolean; registrable?: boolean },
) {
  const queryParams: Record<string, string> = { search, status: 'PUBLISHED' }
  if (options?.include_variants) queryParams.exclude_variants = 'false'
  // Claim/registration pickers: only registrable products, never variant bases.
  if (options?.registrable) {
    queryParams.exclude_variants = 'false'
    queryParams.registrable = 'true'
  }

  return useQuery<PaginatedResponse<Product>>({
    queryKey: ['product-search', queryParams],
    queryFn: () =>
      api
        .get('/products/items/', {
          params: queryParams,
        })
        .then(r => r.data),
    enabled: search.length >= 2,
  })
}

export interface PublishedProductsParams {
  category_uuid?: string | null
  search?: string
  page_size?: string
  include_variants?: boolean
  /**
   * Filter the result to only the variants of a specific parent product.
   * Implies `include_variants=true` so VARIANT rows are not excluded by
   * the backend's default exclusion.
   */
  parent_product_uuid?: string | null
  /**
   * Wizard host attribute values keyed by host attribute ``code_name``.
   * When non-empty, the backend intersects the result with every active
   * global ``AvailabilityRule(scope='product')`` whose conditions match
   * (e.g. a ``structure_type == 'single_family_detached'`` rule narrows
   * to its configured ``category_ids``). Admin browsing should leave this
   * undefined so the catalogue is unaffected.
   */
  host_attrs?: Record<string, unknown> | null
  /**
   * Property postal code from the wizard's host context. When supplied
   * together with ``reference_date`` (and the ``geographic_availability``
   * feature flag is on) the backend resolves the address to a Plant
   * Service Area and intersects the result with every product whose
   * ``ProductAvailability`` window is active for that PSA on the
   * reference date. Either param missing → no PSA narrowing applied.
   */
  postal_code?: string | null
  /** Date the products must be available on (ISO ``YYYY-MM-DD``). */
  reference_date?: string | null
  /** PSA business segment, defaults to ``Shingle`` server-side. */
  business?: string | null
  /** Optional address components used as fallbacks during PSA resolution. */
  county?: string | null
  state?: string | null
  country_code?: string | null
  /**
   * When set, only products belonging to the named product template are
   * returned. Used by the registration wizard when launched from a claim
   * type that is associated with a specific product line (e.g. BFS).
   */
  product_template_name?: string | null
  /**
   * DRF ordering param (one of the endpoint's `ordering_fields`). Use
   * `-registration_count` to surface the most-frequently-registered products
   * first ("frequently used"). Defaults to the endpoint's name ordering.
   */
  ordering?: string | null
  /**
   * Claim/registration pickers: only registrable products, never variant
   * bases. Mirrors `useProductSearch`'s `registrable` option so a picker can
   * browse the cached catalogue instead of gating on a typed search term.
   */
  registrable?: boolean
}

// Exported as shared query options so the post-login cache warmer can prefetch
// the first catalogue page (params undefined) without re-declaring the
// param-building/queryFn (no drift).
export function publishedProductsQueryOptions(params?: PublishedProductsParams) {
  const queryParams: Record<string, string> = { status: 'PUBLISHED', page_size: '100' }
  if (params?.category_uuid) queryParams.category_uuid = params.category_uuid
  if (params?.search && params.search.length >= 2) queryParams.search = params.search
  if (params?.page_size) queryParams.page_size = params.page_size
  if (params?.include_variants || params?.parent_product_uuid) {
    queryParams.exclude_variants = 'false'
  }
  if (params?.parent_product_uuid) {
    queryParams.parent_product_uuid = params.parent_product_uuid
    queryParams.product_type = 'VARIANT'
  }
  if (params?.host_attrs) {
    const cleaned: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(params.host_attrs)) {
      if (v !== undefined && v !== null && v !== '') cleaned[k] = v
    }
    if (Object.keys(cleaned).length > 0) {
      queryParams.host_attrs = JSON.stringify(cleaned)
    }
  }
  // PSA + availability-window narrowing only kicks in when the wizard
  // can supply *both* a postal code and a reference date - anything less
  // and the backend leaves the catalogue untouched.
  if (params?.postal_code && params?.reference_date) {
    queryParams.postal_code = params.postal_code
    queryParams.reference_date = params.reference_date
    if (params.business) queryParams.business = params.business
    if (params.county) queryParams.county = params.county
    if (params.state) queryParams.state = params.state
    if (params.country_code) queryParams.country_code = params.country_code
  }
  if (params?.product_template_name) {
    queryParams.template_name = params.product_template_name
  }
  if (params?.registrable) {
    queryParams.exclude_variants = 'false'
    queryParams.registrable = 'true'
  }
  if (params?.ordering) {
    queryParams.ordering = params.ordering
  }

  return queryOptions<PaginatedResponse<Product>>({
    queryKey: ['published-products', queryParams],
    queryFn: () =>
      api
        .get('/products/items/', { params: queryParams })
        .then(r => r.data),
  })
}

export function usePublishedProducts(params?: PublishedProductsParams) {
  return useQuery<PaginatedResponse<Product>>(publishedProductsQueryOptions(params))
}

export function useConsumerLookup(email: string) {
  const { orgUuid } = useOrg()

  return useQuery<ConsumerAccountSummary | null>({
    queryKey: ['consumer-lookup', orgUuid, email],
    queryFn: () =>
      api
        .get('/registrations/consumers/lookup/', {
          params: { email },
        })
        .then(r => r.data)
        .catch(() => null),
    enabled: !!orgUuid && !!email && email.includes('@'),
  })
}

export function useConsumerDetail(uuid: string | null, params?: Record<string, string>) {
  return useQuery<ConsumerWithHoldings>({
    queryKey: ['consumer-detail', uuid, params],
    queryFn: () =>
      api
        .get(`/registrations/consumers/${uuid}/`, { params })
        .then(r => r.data),
    enabled: !!uuid,
  })
}

// ── Partnerships ─────────────────────────────────────────

export interface PartnershipOption {
  uuid: string
  downstream_organisation: {
    uuid: string
    name: string
  }
  role_name: string
  role_uuid?: string
  is_active: boolean
  // Number of sub-location partnerships beneath this one. >0 means this
  // row is a parent/HQ (and, when include_locations=true, its branches
  // appear as sibling rows) - pickers use this to badge parents as
  // "Headquarters" so the user can pick the HQ vs a specific branch.
  child_partnership_count?: number
  // Flat ``{code_name: value}`` map of PartnershipAttributeValues for
  // this row. Populated by the backend when present. Used by list/
  // picker UIs to render a secondary disambiguator (e.g.
  // ``membership_number``) next to the org name.
  attribute_summary?: Record<string, unknown>
}

/**
 * Options for :func:`usePartnershipSearch`.
 *
 * The hook is a generic primitive - callers opt in to the slice of the
 * partnership space they need. Admin list pages leave defaults alone
 * (parents only, all roles). Flows that need to reach sub-locations
 * (e.g. a registration wizard picking the location that did the
 * install) pass ``includeLocations: true``; flows that target a single
 * partner type pass a ``role`` UUID from the tenant's ExternalRole set.
 */
export interface PartnershipSearchOptions {
  includeLocations?: boolean
  role?: string
  activeOnly?: boolean
}

/** Rows a search picker asks for. Well under `max_page_size` (1000). */
export const PICKER_PAGE_SIZE = 100

export function usePartnershipSearch(
  orgUuid: string | null,
  search: string,
  options: PartnershipSearchOptions = {},
) {
  const { includeLocations = false, role, activeOnly } = options
  return useQuery<PartnershipOption[]>({
    queryKey: [
      'partnerships-search',
      orgUuid,
      search,
      includeLocations,
      role ?? null,
      activeOnly ?? null,
    ],
    queryFn: () => {
      // The default page is 25 and a picker has no paginator, so a term that
      // matched more than that silently lost the tail - and duplicate dealer
      // names (one org per migrated contact) can fill a page on their own.
      const params: Record<string, string> = {
        search,
        page_size: String(PICKER_PAGE_SIZE),
      }
      if (includeLocations) params.include_locations = 'true'
      if (role) params.role = role
      // `is_active`, not `active_only`: the view forwards only `role` /
      // `include_locations` / `search` to the service, so `active_only` was
      // dropped on the floor. `is_active` is in `filterset_fields`, so it
      // narrows in SQL - before pagination, which is the whole point.
      if (activeOnly) params.is_active = 'true'
      return api
        .get(`/organisations/${orgUuid}/partnerships/`, { params })
        .then((r) => r.data.results ?? r.data)
    },
    enabled: !!orgUuid && search.length >= 2,
    staleTime: 30_000,
  })
}

// ── File Uploads ─────────────────────────────────────────

export interface UploadedFile {
  uuid: string
  file_url: string
  file_path: string | null
  thumbnail_url: string | null
  file_name: string
  file_type: string
  file_size: number
  mime_type: string
}

export function useUploadFile() {
  return useMutation<UploadedFile, Error, { file: File; use_case: string }>({
    mutationFn: async ({ file, use_case }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('use_case', use_case)
      const res = await api.post('/files/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
  })
}
